/**
 * PhotoAttribution.js — Renders the TASL credit block for a Wikipedia
 * Commons photo (Title • Author • Source • Licence).
 *
 * Per Creative Commons CC BY-SA 4.0 and Wikimedia Commons reuse
 * guidelines, every photo sourced from Commons must carry attribution
 * the reasonable distance from the image. We display this directly
 * below the avatar on the person's detail page, plus in the global
 * `/credits` index.
 *
 * Props:
 *   attribution: { title, author, license, license_url, source_page }
 *                ↑ from entity.metadata.wikipedia_attribution
 *   compact?: boolean — single-line layout for in-list use (optional)
 */
import { ExternalLink } from 'lucide-react';

export default function PhotoAttribution({ attribution, compact = false }) {
  if (!attribution || !attribution.source_page) return null;

  const {
    title = 'Photograph',
    author = 'Unknown',
    license = 'CC BY-SA',
    license_url,
    source_page,
  } = attribution;

  // Compact single-line variant for /credits list rows.
  if (compact) {
    return (
      <p className="text-[10px] text-muted-foreground leading-snug" data-testid="photo-attribution-compact">
        <span className="font-medium">{title}</span>
        {' · '}
        <span>{author}</span>
        {' · '}
        {license_url ? (
          <a href={license_url} target="_blank" rel="noopener noreferrer nofollow" className="underline hover:text-foreground">
            {license}
          </a>
        ) : (
          <span>{license}</span>
        )}
        {' · '}
        <a href={source_page} target="_blank" rel="noopener noreferrer nofollow" className="underline hover:text-foreground inline-flex items-center gap-0.5">
          source <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </p>
    );
  }

  // Full multi-line block for the entity detail page.
  return (
    <div
      className="mt-3 text-[10px] leading-relaxed text-muted-foreground border-t border-border/40 pt-2"
      data-testid="photo-attribution"
    >
      <p className="font-medium text-muted-foreground/90 mb-0.5">Photo credit</p>
      <p>
        <span className="text-muted-foreground/90">{title}</span>
        {' by '}
        <span className="text-muted-foreground/90">{author}</span>
      </p>
      <p>
        {license_url ? (
          <a
            href={license_url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="underline hover:text-foreground"
          >
            {license}
          </a>
        ) : (
          <span>{license}</span>
        )}
        {' · '}
        <a
          href={source_page}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline hover:text-foreground inline-flex items-center gap-0.5"
        >
          Wikimedia Commons <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </p>
    </div>
  );
}
